import { importProvidersFrom, inject } from '@angular/core';
import { Route, Router, Routes } from '@angular/router';
import { MenuRole } from '@mkp/account/models';
import { selectSelectedAccount } from '@mkp/account/state';
import {
  applicationExistGuard,
  applicationStatusesExistGuard,
} from '@mkp/application/feature-application';
import { isPayPerInviteEnabledGuard } from '@mkp/application/feature-pay-per-invite';
import {
  hasSelectedWorkspaceGuard,
  isBoUserWithoutSelectedAccountGuard,
  redirectToCompanyEditGuard,
  redirectVacancyToWorkspaceGuard,
  selectWorkspaceFromRouteGuard,
} from '@mkp/auth/feature-guards';
import {
  ActiveLegalEntityGuard,
  activeUserGuard,
  boUserGuardFactory,
  hasFullUserInformationGuard,
  hasMultipleWorkspacesGuard,
  hasPartialUserInformationGuard,
  hasTokenInUrl,
  inactiveUserGuard,
  isJoinAccountGuard,
  isUnverifiedUserGuard,
  isVacancyExistingGuard,
  nonProductionGuard,
  permissionGuardFactory,
  preProductionGuard,
  redirectUrlGuard,
  verifiedUserGuard,
  workspacePermissionGuardFactory,
} from '@mkp/core/feature-guard';
import { MetadatasResolver } from '@mkp/metadata/feature-metadata';
import { MetadataStateModule } from '@mkp/metadata/state';
import { AppConfig } from '@mkp/shared/util-app-config';
import { LogoutComponent } from '@mkp/user/feature-logout';
import { LegalEntityVacancyListResolver } from '@mkp/vacancy/feature-vacancy-list';
import { Store } from '@ngrx/store';
import { filter, map, take } from 'rxjs';
import { legacyApplicationLinkGuard } from './legacy-application-link.guard';
import { visitorOnlyUrls } from './visitor-routes';

const { routes } = AppConfig;

const redirectableUrls: string[] = ['', ...Object.values(visitorOnlyUrls)];
const redirectToDashboardRoutes: Routes = redirectableUrls.map((path) => ({
  path,
  // match the full path if the path is empty string, otherwise match the prefix
  pathMatch: path ? 'prefix' : 'full',
  redirectTo: routes.dashboard,
}));

// Legacy: the job ad list moved from `/vacancy` to `/vacancies`. Keeps old bookmarks and marketing
// links alive in both the workspace and the legal-entity context — `redirectTo` is relative, so the
// same object resolves under whichever parent it is nested in. `pathMatch: 'full'` is load-bearing:
// without it this swallows `/vacancy/:id` and the whole shell below it.
const legacyVacancyListRedirect: Route = {
  path: routes.vacancy,
  pathMatch: 'full',
  redirectTo: routes.vacancies,
};

// Legacy: before the `/vacancy/:id` hub, that URL redirected to the applications, so links built on
// it carry a candidate in `?id=`. The guard redirects those or declines; the route never activates.
// A guard rather than `redirectTo`: the router runs no canMatch on a redirect route, so `redirectTo`
// here would take every job ad. `pathMatch: 'full'` keeps it off the hub's children.
const legacyApplicationLinkRedirect: Route = {
  path: `${routes.vacancy}/${routes.id}`,
  pathMatch: 'full',
  canMatch: [legacyApplicationLinkGuard],
  children: [],
};

const ERROR_ROUTES = [
  {
    path: routes.error401,
    loadComponent: () => import('@mkp/core/feature-errors').then((m) => m.Error401Component),
    data: { title: 'META_TITLE.ERROR.401' },
  },
  {
    path: routes.error403,
    loadComponent: () => import('@mkp/core/feature-errors').then((m) => m.Error403Component),
    data: { title: 'META_TITLE.ERROR.403' },
  },
  {
    path: routes.error404,
    loadComponent: () => import('@mkp/core/feature-errors').then((m) => m.Error404Component),
    data: { title: 'META_TITLE.ERROR.404' },
  },
  {
    path: routes.error500,
    loadComponent: () => import('@mkp/core/feature-errors').then((m) => m.Error500Component),
    data: { title: 'META_TITLE.ERROR.500' },
  },
];

export const authenticatedRoutes: Routes = [
  {
    path: '',
    loadComponent: () =>
      import('@mkp/layout/shell-user-layout').then((m) => m.ShellUserLayoutComponent),
    children: [
      ...ERROR_ROUTES,
      {
        path: 'logout',
        component: LogoutComponent,
      },
      {
        path: routes.cookieSettings,
        data: { title: 'META_TITLE.COOKIE_SETTINGS' },
        loadComponent: () =>
          import('@mkp/gdpr/feature-gdpr-consent').then((m) => m.CookieSettingsRouteComponent),
      },
      {
        path: 'mfa-showroom',
        canActivate: [nonProductionGuard],
        loadComponent: () =>
          import('@mkp/auth/feature-mfa-showroom').then((m) => m.MfaShowroomComponent),
      },
      {
        path: routes.vbpShowroom,
        canActivate: [preProductionGuard],
        loadComponent: () =>
          import('@mkp/vbp/feature-showroom').then((m) => m.VbpShowroomComponent),
      },
      {
        path: routes.ui,
        canMatch: [nonProductionGuard],
        loadChildren: () =>
          import('@mkp/design-system/feature-core').then((m) => m.featureDesignSystemRoutes),
      },
      {
        path: routes.redeemInvite,
        canActivate: [hasTokenInUrl],
        loadComponent: () =>
          import('@mkp/user-account-membership/feature-user-management').then(
            (m) => m.RedeemInviteComponent
          ),
      },
      {
        path: routes.invalidInvite,
        loadComponent: () =>
          import('@mkp/user-account-membership/feature-user-management').then(
            (m) => m.InvalidInviteComponent
          ),
      },
      {
        path: routes.verifyIdentity,
        canMatch: [isUnverifiedUserGuard],
        loadComponent: () =>
          import('@mkp/onboarding/feature-verify-identity').then((m) => m.VerifyIdentityComponent),
      },
      // ALL ROUTES BELOW REQUIRE A VERIFIED USER
      {
        path: '',
        canMatch: [verifiedUserGuard],
        children: [
          {
            path: routes.unsubscribe,
            canMatch: [inactiveUserGuard],
            loadComponent: () =>
              import('@mkp/user/feature-unsubscribe').then((m) => m.UnsubscribeComponent),
            data: { title: 'META_TITLE.UNSUBSCRIBE' },
          },
          {
            path: routes.claimCompany,
            canLoad: [inactiveUserGuard],
            canActivate: [inactiveUserGuard],
            data: { title: 'META_TITLE.CLAIMCOMPANY' },
            loadComponent: () =>
              import('@mkp/onboarding/feature-claim-company').then((m) => m.ClaimCompanyComponent),
          },
          // ALL ROUTES BELOW REQUIRE A CLAIMED COMPANY
          {
            path: '',
            canMatch: [activeUserGuard],
            children: [
              {
                path: routes.incompleteOnboarding,
                canMatch: [hasPartialUserInformationGuard],
                loadComponent: () =>
                  import('@mkp/onboarding/feature-incomplete').then(
                    (c) => c.IncompleteOnboardingComponent
                  ),
              },
              // ALL ROUTES BELOW REQUIRE A FULL INFORMATION USER
              {
                path: '',
                canMatch: [hasFullUserInformationGuard],
                children: [
                  ...redirectToDashboardRoutes,
                  {
                    path: routes.selectAccountMembership,
                    canMatch: [hasMultipleWorkspacesGuard],
                    loadComponent: () =>
                      import('@mkp/user-account-membership/feature-select-workspace').then(
                        (m) => m.SelectWorkspaceComponent
                      ),
                  },
                  {
                    path: routes.dashboard,
                    canMatch: [redirectUrlGuard, isBoUserWithoutSelectedAccountGuard],
                    data: { title: 'META_TITLE.DASHBOARD' },
                    loadChildren: () =>
                      import('@mkp/user/feature-bo-dashboard').then((m) => m.BoDashboardModule),
                  },
                  {
                    path: routes.dashboard,
                    canMatch: [hasSelectedWorkspaceGuard, redirectUrlGuard],
                    canActivate: [
                      () => {
                        const store = inject(Store);
                        const router = inject(Router);
                        return store.select(selectSelectedAccount).pipe(
                          filter((account) => !!account),
                          take(1),
                          map((account) =>
                            router.createUrlTree([routes.workspace, account!.id, routes.dashboard])
                          )
                        );
                      },
                    ],
                    data: { title: 'META_TITLE.DASHBOARD' },
                    loadComponent: () =>
                      import('@mkp/user/feature-dashboard').then((m) => m.DashboardComponent),
                  },
                  {
                    path: `${AppConfig.routes.claimManagement}/${AppConfig.routes.claimCompany}`,
                    canMatch: [boUserGuardFactory(routes.dashboard)],
                    loadComponent: () =>
                      import('@mkp/onboarding/feature-bo-claim-company').then(
                        (m) => m.BoClaimCompanyComponent
                      ),
                  },
                  {
                    path: `${AppConfig.routes.claimManagement}/${AppConfig.routes.claimCompany}/${AppConfig.routes.createCompany}`,
                    canMatch: [boUserGuardFactory(routes.dashboard)],
                    loadComponent: () =>
                      import('@mkp/onboarding/feature-bo-create-company').then(
                        (m) => m.BoCreateCompanyComponent
                      ),
                  },
                  {
                    path: AppConfig.routes.claimManagement,
                    canMatch: [boUserGuardFactory(routes.dashboard)],
                    loadChildren: () =>
                      import('@mkp/legal-entity/feature-claim-management/modules').then(
                        (m) => m.LegalEntityModule
                      ),
                  },
                  {
                    path: routes.user,
                    loadChildren: () =>
                      import('@mkp/user-account-membership/feature-user-account-membership').then(
                        (m) => m.featureUserAccountMembershipRoutes
                      ),
                  },
                  {
                    path: `${routes.workspace}/:workspaceId`,
                    children: [
                      {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: routes.dashboard,
                      },
                      {
                        path: routes.dashboard,
                        data: { title: 'META_TITLE.DASHBOARD' },
                        canMatch: [redirectUrlGuard],
                        canActivate: [selectWorkspaceFromRouteGuard],
                        loadComponent: () =>
                          import('@mkp/user/feature-dashboard').then((m) => m.DashboardComponent),
                      },
                      {
                        path: `${AppConfig.routes.userManagement}`,
                        data: { title: 'META_TITLE.USER_MANAGEMENT' },
                        canActivate: [
                          workspacePermissionGuardFactory(MenuRole.UserManagement),
                          isJoinAccountGuard,
                        ],
                        loadChildren: () =>
                          import('@mkp/user-account-membership/feature-user-management').then(
                            (m) => m.featureUserManagementRoutes
                          ),
                      },
                      {
                        path: `${AppConfig.routes.accountSettings}`,
                        data: { title: 'META_TITLE.ACCOUNT_SETTINGS' },
                        canActivate: [workspacePermissionGuardFactory(MenuRole.AccountSettings)],
                        loadChildren: () =>
                          import('@mkp/account/feature-account-settings').then(
                            (m) => m.accountFeatureAccountSettingsRoutes
                          ),
                      },
                      {
                        path: `${AppConfig.routes.creditManagement}`,
                        data: { title: 'META_TITLE.CREDIT_OVERVIEW' },
                        canActivate: [workspacePermissionGuardFactory(MenuRole.Wallet)],
                        loadChildren: () =>
                          import('@mkp/credit/feature-credit-management-page').then(
                            (m) => m.CreditManagementModule
                          ),
                      },
                      legacyApplicationLinkRedirect,
                      {
                        path: `${routes.vacancy}/${routes.id}/${routes.applications}`,
                        data: { title: 'META_TITLE.APPLICATIONS' },
                        canActivate: [
                          ActiveLegalEntityGuard,
                          isVacancyExistingGuard,
                          selectWorkspaceFromRouteGuard,
                          applicationStatusesExistGuard,
                          applicationExistGuard,
                        ],
                        children: [
                          {
                            path: '',
                            pathMatch: 'full',
                            loadComponent: () => import('@mkp/application/feature-application'),
                          },
                          {
                            path: AppConfig.routes.payPerInvite,
                            canMatch: [isPayPerInviteEnabledGuard],
                            loadComponent: () => import('@mkp/application/feature-pay-per-invite'),
                          },
                        ],
                      },
                      {
                        path: routes.vacancies,
                        canActivate: [selectWorkspaceFromRouteGuard],
                        // Stop tracking in the row menu reads metadata, and the list sits
                        // outside the vacancy shell that otherwise loads it.
                        providers: [importProvidersFrom(MetadataStateModule)],
                        resolve: { metadatas: MetadatasResolver },
                        loadChildren: () =>
                          import('@mkp/vacancy/feature-vacancy-list').then(
                            (m) => m.vacancyListRoutes
                          ),
                      },
                      legacyVacancyListRedirect,
                      {
                        path: routes.vacancy,
                        canActivate: [selectWorkspaceFromRouteGuard],
                        loadChildren: () =>
                          import('@mkp/vacancy/shell-vacancy').then((m) => m.vacancyRoutes),
                      },
                    ],
                  },
                  {
                    path: routes.company,
                    canActivate: [
                      hasSelectedWorkspaceGuard,
                      permissionGuardFactory(MenuRole.CompanyProfile),
                    ],
                    children: [
                      {
                        path: '',
                        pathMatch: 'full',
                        canMatch: [redirectToCompanyEditGuard],
                        children: [],
                      },
                      {
                        path: 'edit',
                        loadChildren: () =>
                          import('@mkp/company/feature-company-form').then(
                            (m) => m.CompanyFormModule
                          ),
                      },
                    ],
                  },
                  {
                    path: `${AppConfig.routes.legalEntity}/:legalEntityId`,
                    resolve: { legalEntity: LegalEntityVacancyListResolver },
                    children: [
                      {
                        path: routes.vacancies,
                        loadComponent: () =>
                          import('@mkp/vacancy/feature-vacancy-list').then(
                            (c) => c.VacancyListMultiworkspaceComponent
                          ),
                      },
                      legacyVacancyListRedirect,
                    ],
                  },
                  // redirect for link with /vacancy=?search=nn
                  {
                    path: routes.vacancy,
                    canMatch: [redirectVacancyToWorkspaceGuard],
                    children: [],
                  },
                  // Guards and services now redirect to /vacancies, so the workspace-agnostic form
                  // has to resolve too — this prefixes it to the selected workspace's list.
                  {
                    path: routes.vacancies,
                    canMatch: [redirectVacancyToWorkspaceGuard],
                    children: [],
                  },
                ],
              },
            ],
          },
        ],
      },
      { path: '**', redirectTo: '/' + routes.error404 },
    ],
  },
];
